import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, KpiCard, KpiReihe,
  LoadingState, StatusBadge, WertEinheitFeld, type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * DALI-Linien, Adressvergabe und Lichtszenen (Paket H2, Migration 0575).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS DIESE SEITE ZEIGT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Oben der Linienbestand: je Gateway/Verteiler gebildete DALI-Busse mit
 * Teilnehmerzahl und Reserve. „DALI-Linien bilden" rechnet deterministisch —
 * derselbe Bestand ergibt IMMER dieselben Adressen, ein zweiter Lauf
 * verschiebt keine.
 *
 * Unten der Szenen-Editor: je Raum werden die Vorgabeszenen der
 * Raum-Nutzungsart übernommen (Fachplanerfrage 278) und lassen sich von Hand
 * in Dimmwert und Farbtemperatur ändern.
 *
 * Leuchten ohne Verteiler bekommen KEINE Linie — das ist eine Lücke
 * (Abbruchregel), keine erfundene Zuordnung, und wird oben ausgewiesen.
 *
 * ─── Umzug auf die Musterbibliothek (Block U, Paket U6b) ───────────────────
 * Dimmwert und Farbtemperatur stehen als `WertEinheitFeld` mit sichtbarer
 * Einheit (% bzw. K) statt als nacktes Zahlenfeld, dessen Einheit nur in der
 * Spaltenüberschrift stand.
 */
export function ElektroDali() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [linienDaten, setLinienDaten] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [letzterLauf, setLetzterLauf] = useState<any | null>(null);

  const [raum, setRaum] = useState('');
  const [szenen, setSzenen] = useState<any[]>([]);
  const [ladeSzenen, setLadeSzenen] = useState(false);
  const [szenenRaum, setSzenenRaum] = useState<string | null>(null);

  const ladeLinien = useCallback(() => {
    if (!projekt) return;

    api.elektroDaliLinien(projekt.id)
      .then((x) => { setLinienDaten(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id]);

  useEffect(() => { ladeLinien(); }, [ladeLinien]);

  const linienBilden = async (nurPruefen: boolean) => {
    if (!projekt) return;

    setLaeuft(true);

    try {
      const ergebnis = await api.elektroDaliLinienBilden(projekt.id, nurPruefen);
      setLetzterLauf(ergebnis ?? null);
      if (!nurPruefen) ladeLinien();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const ladeSzenenFuerRaum = () => {
    if (!projekt || !raum.trim()) return;

    setLadeSzenen(true);

    api.elektroLichtszenen(projekt.id, raum.trim())
      .then((x) => { setSzenen(x); setSzenenRaum(raum.trim()); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => setLadeSzenen(false));
  };

  const vorgabeUebernehmen = async (ueberschreiben: boolean) => {
    if (!projekt || !raum.trim()) return;

    try {
      const ergebnis = await api.elektroLichtszenenVorgabeUebernehmen(
        projekt.id, raum.trim(), ueberschreiben);
      setFehler(null);
      window.alert(ergebnis ? ergebnis.message : 'Übernommen.');
      ladeSzenenFuerRaum();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  const szeneAendern = async (
    id: string, feld: 'dimmwertProzent' | 'farbtemperatur', wert: number,
  ) => {
    if (!projekt) return;

    try {
      await api.elektroLichtszeneAendern(projekt.id, id, { [feld]: wert });
      ladeSzenenFuerRaum();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  const linien: any[] = useMemo(() => linienDaten?.linien ?? [], [linienDaten]);

  const gesamtTeilnehmer = useMemo(
    () => linien.reduce((s, l) => s + l.teilnehmer, 0), [linien]);

  const linienspalten: Spalte<any>[] = useMemo(() => [
    { schluessel: 'kennzeichen', titel: 'Linie', immerSichtbar: true, wert: (l) => l.kennzeichen },
    { schluessel: 'verteiler', titel: 'Verteiler', wert: (l) => l.verteiler },
    {
      schluessel: 'teilnehmer', titel: 'Teilnehmer', art: 'zahl',
      wert: (l) => l.teilnehmer,
    },
    {
      schluessel: 'kapazitaet', titel: 'Kapazität', art: 'zahl',
      titelHinweis: 'Höchstzahl der Kurzadressen einer DALI-Linie',
      wert: (l) => l.maxTeilnehmer,
    },
    {
      schluessel: 'reserve', titel: 'Reserve', art: 'zahl', einheit: '%',
      wert: (l) => l.reserveProzent,
    },
    {
      schluessel: 'hinweis', titel: 'Hinweis', wert: (l) => l.hinweis,
      inhalt: (l) => (l.hinweis
        ? <StatusBadge zustand="warnung" text={l.hinweis} klein />
        : <span className="schwach">—</span>),
    },
  ], []);

  const szenenspalten: Spalte<any>[] = useMemo(() => [
    { schluessel: 'nr', titel: 'Szene', art: 'zahl', breite: '90px', wert: (s) => s.szeneNr },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', immerSichtbar: true, wert: (s) => s.bezeichnung },
    {
      schluessel: 'gruppe', titel: 'Gruppe', wert: (s) => s.zielGruppe,
      inhalt: (s) => <>{s.zielGruppe ?? '–'}</>,
    },
    {
      schluessel: 'dimm', titel: 'Dimmwert', einheit: '%', breite: '170px',
      sortierbar: false, wert: (s) => s.dimmwertProzent,
      inhalt: (s) => (
        <WertEinheitFeld
          beschriftung={`Dimmwert Szene ${s.szeneNr}`}
          einheit="%"
          wert={s.dimmwertProzent}
          min={0}
          max={100}
          breite="voll"
          beiAenderung={(w) => {
            if (w !== null && w !== s.dimmwertProzent) {
              szeneAendern(s.id, 'dimmwertProzent', w);
            }
          }}
        />
      ),
    },
    {
      schluessel: 'farbtemperatur', titel: 'Farbtemperatur', einheit: 'K',
      breite: '190px', sortierbar: false, wert: (s) => s.farbtemperatur,
      inhalt: (s) => (
        <WertEinheitFeld
          beschriftung={`Farbtemperatur Szene ${s.szeneNr}`}
          einheit="K"
          wert={s.farbtemperatur ?? null}
          min={1800}
          max={6500}
          breite="voll"
          beiAenderung={(w) => {
            if (w !== null && w > 0 && w !== s.farbtemperatur) {
              szeneAendern(s.id, 'farbtemperatur', w);
            }
          }}
        />
      ),
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', wert: (s) => s.herkunft,
      inhalt: (s) => <span className="schwach">{s.herkunft}</span>,
    },
  ], [projekt?.id]);

  const kopf = (
    <PageHeader
      titel="DALI-Linien und Lichtszenen"
      beschreibung={'Linienbildung je Gateway/Verteiler (höchstens 64 Kurzadressen, 20 % '
        + 'Reserve — Fachplanerfrage 277, unbestätigt) und Lichtszenen je Raum, '
        + 'vorbelegt aus der Raum-Nutzungsart (Fachplanerfrage 278, unbestätigt).'}
      aktionen={projekt ? (
        <>
          <button className="m-knopf" disabled={laeuft} onClick={() => linienBilden(true)}>
            Probe (nichts schreiben)
          </button>
          <button
            className="m-knopf m-knopf-haupt"
            disabled={laeuft}
            onClick={() => linienBilden(false)}
          >
            DALI-Linien bilden und schreiben
          </button>
          <button className="m-knopf" onClick={() => api.elektroDaliBerichtLaden(projekt.id)}>
            Bericht ELT-DALI als PDF
          </button>
        </>
      ) : undefined}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die DALI-Linien konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => { setFehler(null); ladeLinien(); }}
        />
      </>
    );
  }

  if (!projekt || !linienDaten) {
    return (
      <>
        {kopf}
        <LoadingState text="DALI-Linien werden geladen …" zeilen={6} spalten={5} />
      </>
    );
  }

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard titel="DALI-fähige Leuchten" wert={linienDaten.leuchtenGesamt} />
        <KpiCard
          titel="ohne Verteiler"
          wert={linienDaten.leuchtenOhneVerteiler}
          vergleich="bekommen keine Linie"
          zustand={linienDaten.leuchtenOhneVerteiler > 0 ? 'warnung' : 'gut'}
          zustandText={linienDaten.leuchtenOhneVerteiler > 0
            ? 'Lücke, keine erfundene Zuordnung' : 'keine Lücke'}
        />
        <KpiCard titel="gebildete Linien" wert={linien.length} />
        <KpiCard
          titel="adressierte Leuchten" wert={gesamtTeilnehmer}
          vergleich={`von ${(linienDaten.leuchtenGesamt ?? 0).toLocaleString('de-DE')}`}
        />
      </KpiReihe>

      {letzterLauf && (
        <div className="hinweis" style={{ display: 'block', marginTop: 10 }}>
          {letzterLauf.message}
          {letzterLauf.hinweise?.length > 0 && (
            <ul>
              {letzterLauf.hinweise.map((h: string, i: number) => <li key={i}>{h}</li>)}
            </ul>
          )}
        </div>
      )}

      <DataTable
        name="DALI-Linien"
        spalten={linienspalten}
        zeilen={linien}
        schluessel={(l) => String(l.id)}
        ausfuhr
        ausfuhrDateiname="dali-linien"
        laedt={laeuft}
        leer={(
          <EmptyState
            titel="Für dieses Modell wurde noch keine DALI-Linie gebildet."
            erklaerung="Die Linienbildung fasst die DALI-fähigen Leuchten je Gateway/Verteiler zusammen und rechnet deterministisch — ein zweiter Lauf verschiebt keine Adresse."
            aktion={(
              <button
                className="m-knopf m-knopf-haupt"
                disabled={laeuft}
                onClick={() => linienBilden(false)}
              >
                DALI-Linien bilden
              </button>
            )}
          />
        )}
      />

      {/* --- Szenen-Editor je Raum ------------------------------------------ */}
      <ContentSection
        titel="Lichtszenen je Raum"
        beschreibung="Vorbelegt aus der Raum-Nutzungsart; Dimmwert und Farbtemperatur lassen sich je Szene ändern."
        werkzeuge={(
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Feld
              beschriftung="Raumnummer"
              art="suche"
              wert={raum}
              beiAenderung={(w) => setRaum(w as string)}
              platzhalter="z. B. E00-106"
              breite="drittel"
            />
            <button className="m-knopf" disabled={!raum.trim() || ladeSzenen} onClick={ladeSzenenFuerRaum}>
              Szenen laden
            </button>
            <button className="m-knopf" disabled={!raum.trim()} onClick={() => vorgabeUebernehmen(false)}>
              Vorgabeszenen übernehmen
            </button>
            <button className="m-knopf" disabled={!raum.trim()} onClick={() => vorgabeUebernehmen(true)}>
              Vorgabeszenen übernehmen (überschreiben)
            </button>
          </div>
        )}
        nackt
      >
        <DataTable
          name={szenenRaum ? `Lichtszenen ${szenenRaum}` : 'Lichtszenen'}
          spalten={szenenspalten}
          zeilen={szenen}
          schluessel={(s) => String(s.id)}
          laedt={ladeSzenen}
          suche={false}
          leer={(
            <EmptyState
              titel={szenenRaum
                ? `Für ${szenenRaum} ist noch keine Lichtszene hinterlegt.`
                : 'Noch kein Raum gewählt.'}
              erklaerung={szenenRaum
                ? 'Die Vorgabeszenen der Raum-Nutzungsart lassen sich übernehmen und danach von Hand ändern.'
                : 'Raumnummer eingeben und „Szenen laden“ — dann stehen die Szenen dieses Raumes hier.'}
              aktion={szenenRaum ? (
                <button className="m-knopf m-knopf-haupt" onClick={() => vorgabeUebernehmen(false)}>
                  Vorgabeszenen übernehmen
                </button>
              ) : undefined}
            />
          )}
        />
      </ContentSection>
    </>
  );
}
